import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABEL } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Plane,
  CalendarPlus,
  CalendarDays,
  Car,
  Users,
  Ticket,
  Sparkles,
  KeyRound,
  LogOut,
  Menu,
  X,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/trasferte", label: "Le mie trasferte", icon: Plane, testid: "nav-trips" },
  { to: "/nuova-trasferta", label: "Nuova trasferta", icon: CalendarPlus, testid: "nav-new-trip" },
  { to: "/prenotazioni", label: "Prenotazioni", icon: Ticket, testid: "nav-bookings" },
  { to: "/assistente-ai", label: "Assistente AI", icon: Sparkles, testid: "nav-ai" },
];

const ADMIN_NAV = [
  { to: "/calendario-team", label: "Calendario Team", icon: CalendarDays, testid: "nav-team-cal", roles: ["responsabile", "amministratore"] },
  { to: "/auto-aziendali", label: "Auto aziendali", icon: Car, testid: "nav-cars", roles: ["amministratore"] },
  { to: "/utenti", label: "Utenti", icon: Users, testid: "nav-users", roles: ["amministratore", "responsabile"] },
];

export function Layout({ children }) {
  const { user, logout, activeCompany, setActiveCompany } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const effectiveCompany = activeCompany || user?.active_company || user?.company || "COSMA";
  const isConsolidated = effectiveCompany === "TUTTE";

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, user?.id]);

  const closeDrawer = () => setOpen(false);
  const handleLogout = async () => {
    closeDrawer();
    await logout();
    navigate("/login", { replace: true });
  };

  const nav = [...NAV, ...ADMIN_NAV.filter((n) => n.roles.includes(user?.role))];

  const NavLinks = () => (
    <nav className="flex flex-col gap-1">
      {nav.map((item) => {
        const active = location.pathname === item.to;
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={closeDrawer}
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              active ? "bg-[#E30613] text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
          >
            <Icon size={18} />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );

  const initials = (user?.name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 bg-slate-900 text-white flex-col justify-between p-4 fixed inset-y-0 left-0 z-40">
        <div>
          <div className="px-2 py-4 mb-2">
            <div className="font-display text-xl font-extrabold leading-tight">
              <span className="text-[#E30613]">{isConsolidated ? "COSMA • RIMAR" : effectiveCompany}</span> TRAVEL
            </div>
            <div className="text-xs tracking-[0.2em] text-slate-400 font-medium">DESK</div>
          </div>
          <NavLinks />
        </div>
        <div className="border-t border-slate-800 pt-4">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="h-9 w-9 rounded-full bg-[#E30613] flex items-center justify-center text-sm font-semibold">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-slate-400">{ROLE_LABEL[user?.role]}</div>
            </div>
          </div>
          <Link
            to="/change-password"
            data-testid="change-password-nav-desktop"
            onClick={closeDrawer}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <KeyRound size={18} /> Cambia password
          </Link>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut size={18} /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div data-testid="mobile-drawer-backdrop" className="absolute inset-y-0 left-64 right-0 z-0 bg-black/50" onClick={closeDrawer} />
          <aside className="relative z-10 w-64 bg-slate-900 text-white flex flex-col justify-between p-4">
            <div>
              <div className="flex items-center justify-between px-2 py-3 mb-2">
                <div className="font-display text-lg font-extrabold">
                  <span className="text-[#E30613]">{isConsolidated ? "COSMA • RIMAR" : effectiveCompany}</span> DESK
                </div>
                <button data-testid="mobile-drawer-close-button" onClick={() => setOpen(false)}><X size={20} /></button>
              </div>
              <NavLinks />
            </div>
            <Link
              to="/change-password"
              data-testid="change-password-nav-mobile"
              onClick={closeDrawer}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
            >
              <KeyRound size={18} /> Cambia password
            </Link>
            <button
              data-testid="mobile-logout-button"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800"
            >
              <LogOut size={18} /> Esci
            </button>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30">
          <button className="lg:hidden text-slate-700" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu size={22} />
          </button>
          <div className="font-display font-semibold text-slate-900 hidden sm:block">
            {isConsolidated ? "COSMA • RIMAR TRAVEL DESK" : `${effectiveCompany} TRAVEL DESK`}
          </div>
          <div className="flex items-center gap-3">
            {user?.can_switch_company && (
              <select
                aria-label="Azienda attiva"
                data-testid="active-company-select"
                value={effectiveCompany}
                onChange={(event) => setActiveCompany(event.target.value)}
                className="hidden sm:block rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700"
              >
                {user.allowed_companies?.map((company) => <option key={company} value={company}>{company}</option>)}
                <option value="TUTTE">Tutte le aziende</option>
              </select>
            )}
            {isConsolidated && <span className="hidden sm:inline text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">Vista consultiva</span>}
            <span className="hidden sm:inline text-xs font-medium text-slate-500 border border-slate-200 rounded-full px-3 py-1">
              {ROLE_LABEL[user?.role]}
            </span>
            <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
              {initials}
            </div>
          </div>
        </header>
        <main key={effectiveCompany} className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full">{children}</main>
      </div>
    </div>
  );
}
